/**
 * ChipGroup - Container for Chip Components
 *
 * Manages layout and spacing for groups of Chip components.
 * Renders as Box with flex-wrap layout.
 *
 * @example
 * <ChipGroup label="Room Layout">
 *   <Chip selected={true}>1K</Chip>
 *   <Chip selected={false}>2LDK</Chip>
 *   <Chip selected={false}>3LDK</Chip>
 * </ChipGroup>
 */
import { forwardRef } from "react";
import { Box } from "@/shared/ui/base";
import { Label } from "./Label";
import { cn } from "@/shared/lib/utils/cn";
import type { BaseComponentProps } from "@/shared/ui/base";

export interface ChipGroupProps extends BaseComponentProps {
  /** Optional label for the group */
  label?: string;
  /** Required indicator for label */
  required?: boolean;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * ChipGroup component
 * Renders as a Box container for Chip components
 */
export const ChipGroup = forwardRef<HTMLDivElement, ChipGroupProps>(
  ({ label, required, disabled, className, children, ...props }, ref) => {
    return (
      <Box ref={ref} className={cn("space-y-2", className)} {...props}>
        {label && (
          <Label required={required} disabled={disabled}>
            {label}
          </Label>
        )}
        <Box className="flex flex-wrap gap-2">{children}</Box>
      </Box>
    );
  },
);

ChipGroup.displayName = "ChipGroup";
